import {
  createContext,
  type FC,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";

import type { Graph } from "src/components/charts/engine/types";
import type { ChartTab, FunnelStepGraphData } from "src/pages/analytics/types";

type ChartData =
  | {
      funnelsData: FunnelStepGraphData[];
      chartTab: ChartTab.Funnel;
    }
  | {
      graph: Graph;
      chartTab: ChartTab.Insights | ChartTab.SplitsMeasurement;
    };

type ChartDataContextType = {
  chartData: ChartData | null;
  setChartData: (data: ChartData | null) => void;
  isPolling: boolean;
  setIsPolling: (isPolling: boolean) => void;
  runQuery: () => void;
  setRunQuery: (runQuery: () => void) => void;
  validQuery: boolean;
  setValidQuery: (validQuery: boolean) => void;
  lastRanAt: Date | null;
  setLastRanAt: (lastRanAt: Date | null) => void;
  staleQuery: boolean;
  setStaleQuery: (staleQuery: boolean) => void;
};

const noop = () => {};

const defaultContextValue: ChartDataContextType = {
  chartData: null,
  setChartData: noop,
  isPolling: false,
  setIsPolling: noop,
  runQuery: noop,
  setRunQuery: noop,
  validQuery: false,
  setValidQuery: noop,
  lastRanAt: null,
  setLastRanAt: noop,
  staleQuery: false,
  setStaleQuery: noop,
};

const ChartDataContext =
  createContext<ChartDataContextType>(defaultContextValue);
ChartDataContext.displayName = "ChartDataContext";

export const useChartDataContext = () => {
  const context = useContext(ChartDataContext);
  if (!context) {
    throw new Error(
      "useChartDataContext must be used within a ChartDataProvider",
    );
  }
  return context;
};

type ChartDataProviderProps = {
  children: ReactNode;
};

export const ChartDataProvider: FC<ChartDataProviderProps> = ({ children }) => {
  const [chartData, setChartDataState] = useState<ChartData | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [validQuery, setValidQuery] = useState(false);
  const [lastRanAt, setLastRanAt] = useState<Date | null>(null);
  const [staleQuery, setStaleQuery] = useState(false);
  const runQueryRef = useRef<() => void>(noop);
  const runQuery = useCallback(() => runQueryRef.current(), []);
  const setRunQuery = useCallback((fn: () => void) => {
    runQueryRef.current = fn;
  }, []);

  const setChartData = useCallback((data: ChartData | null) => {
    setChartDataState(data);
  }, []);

  const value = useMemo(
    () => ({
      chartData,
      setChartData,
      isPolling,
      setIsPolling,
      runQuery,
      setRunQuery,
      validQuery,
      setValidQuery,
      lastRanAt,
      setLastRanAt,
      staleQuery,
      setStaleQuery,
    }),
    [
      chartData,
      setChartData,
      isPolling,
      runQuery,
      setRunQuery,
      validQuery,
      lastRanAt,
      staleQuery,
    ],
  );

  return (
    <ChartDataContext.Provider value={value}>
      {children}
    </ChartDataContext.Provider>
  );
};
